import React, { lazy, Suspense } from "react";
import { Navigate, Outlet, RouteObject } from "react-router-dom";

import BriefcaseBlueIcon from "@/assets/icons/briefcase-blue-icon.svg?react";
import BriefcaseIcon from "@/assets/icons/briefcase-icon.svg?react";
import DashboardFillIcon from "@/assets/icons/dashboard-fill-icon.svg?react";
import DashboardIcon from "@/assets/icons/dashboard-icon.svg?react";
import EditIcon from "@/assets/icons/edit-icon.svg?react";
import EventBlueIcon from "@/assets/icons/event-blue-icon.svg?react";
import EventIcon from "@/assets/icons/event-icon.svg?react";
import IndexBlueIcon from "@/assets/icons/index-blue-icon.svg?react";
import IndexIcon from "@/assets/icons/index-icon.svg?react";
import Integration2Icon from "@/assets/icons/integration-2-icon.svg?react";
import IntegrationIcon from "@/assets/icons/integration2-icon.svg?react";
import SettingsIcon from "@/assets/icons/setting-icon.svg?react";
import SettingsBlueIcon from "@/assets/icons/settings-blue-icon.svg?react";
import SocialSupport2Icon from "@/assets/icons/social-support-2-icon.svg?react";
import SocialSupportBlueIcon from "@/assets/icons/social-support-blue-icon.svg?react";
import StarsIcon from "@/assets/icons/stars-icon.svg?react";
import YouthBlueIcon from "@/assets/icons/youth-blue-icon.svg?react";
import YouthIcon from "@/assets/icons/youth-icon.svg?react";
import YouthListFillIcon from "@/assets/icons/youth-list-fill-icon.svg?react";
import YouthListIcon from "@/assets/icons/youth-list2.svg?react";
import { AccountType } from "@/auth/types/LoginAuth";
import { AccessLevel } from "@/constants/accessLevel";
import { yetakchiAdmin } from "@/routes/sections/yetakchi-admin";
import { yetakchiDashboard } from "@/routes/sections/yetakchi-dashboard";
import { shouldShowRoute } from "@/utils/checkModulePermissions";
import { lazyWithReload } from "@/utils/lazyWithReload";

export interface RouteConfig {
  path: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Icon to show when this sidebar item is selected. If omitted, the default `icon` is used (styled by CSS). */
  iconActive?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  component?: React.LazyExoticComponent<React.ComponentType<any>>; // Optional - if empty, uses Outlet
  showSidebar: boolean;
  description?: string;
  children?: RouteConfig[];
  index?: boolean; // For index routes
  requiredAccessLevels?: AccessLevel[]; // If set, only these access levels can see this route
  requiredUserIds?: number[]; // If set, only these user IDs can see this route
}

export const getMainRoutes = (t: (key: string) => string): RouteConfig[] => [
  {
    path: "/dashboard",
    label: t("sidebar.dashboard"),
    icon: DashboardIcon,
    iconActive: DashboardFillIcon,
    component: lazyWithReload(() => import("@/pages/Protected/Dashboard")),
    showSidebar: true,
  },
  {
    path: "/youth-list",
    label: t("sidebar.youthList"),
    icon: YouthListIcon,
    iconActive: YouthListFillIcon,
    // component is empty - MainLayout's Outlet will handle children
    showSidebar: true,
    children: [
      {
        path: "",
        label: t("sidebar.youthList"),
        icon: YouthListIcon,
        component: lazyWithReload(() => import("@/pages/Protected/YouthList")),
        showSidebar: false,
        index: true,
      },
      {
        path: ":youthId",
        label: t("sidebar.youthDetail"),
        icon: YouthListIcon, // Not used for children, but required by interface
        component: lazyWithReload(
          () => import("@/pages/Protected/YouthDetails"),
        ),
        showSidebar: false,
        // Example: You can add children to child routes
        // children: [
        //   {
        //     path: "edit",
        //     label: "Edit Youth",
        //     icon: YouthListIcon,
        //     component: lazy(() => import("@/pages/Protected/YouthEdit")),
        //     showSidebar: false,
        //   },
        // ],
      },
    ],
  },
  {
    path: "/integrations",
    label: t("sidebar.integrations"),
    icon: IntegrationIcon,
    iconActive: Integration2Icon,
    // component is empty - MainLayout's Outlet will handle children
    showSidebar: true,
    children: [
      {
        path: "",
        label: t("sidebar.integrations"),
        icon: IntegrationIcon,
        component: lazyWithReload(
          () => import("@/pages/Protected/Integrations"),
        ),
        showSidebar: false,
        index: true,
      },
      {
        path: ":id",
        label: t("sidebar.integrationDetail"),
        icon: IntegrationIcon,
        component: lazyWithReload(
          () => import("@/pages/Protected/IntegrationDetail"),
        ),
        showSidebar: false,
      },
    ],
  },
  {
    path: "/employees",
    label: t("sidebar.employees"),
    icon: BriefcaseIcon,
    iconActive: BriefcaseBlueIcon,
    showSidebar: true,
    children: [
      {
        path: "",
        label: t("sidebar.employees"),
        icon: BriefcaseIcon,
        component: lazyWithReload(() => import("@/pages/Protected/Employees")),
        showSidebar: false,
        index: true,
      },
      {
        path: ":employeeId",
        label: t("sidebar.employees"),
        icon: BriefcaseIcon,
        component: lazyWithReload(
          () => import("@/pages/Protected/EmployeeDetails"),
        ),
        showSidebar: false,
      },
    ],
  },
  {
    path: "/social-support",
    label: t("sidebar.socialSupport"),
    icon: SocialSupport2Icon,
    iconActive: SocialSupportBlueIcon,
    // component is empty - MainLayout's Outlet will handle children
    showSidebar: true,
    children: [
      {
        path: "applications",
        label: t("sidebar.applications"),
        icon: SocialSupport2Icon,
        // No component property - defaults to OutletComponent via getRouteObjects
        showSidebar: true,
        children: [
          {
            path: "", // Index route for /social-support/applications
            label: t("sidebar.applications"),
            icon: SocialSupport2Icon,
            component: lazyWithReload(
              () => import("@/pages/Protected/SocialSupportApplications"),
            ),
            showSidebar: false,
            index: true,
          },
          {
            path: ":applicationId",
            label: t("sidebar.serviceApplications"),
            icon: SocialSupport2Icon,
            component: lazyWithReload(
              () => import("@/pages/Protected/ServiceApplications"),
            ),
            showSidebar: false,
          },
        ],
      },
      {
        path: "budget",
        label: t("sidebar.budget"),
        icon: SocialSupport2Icon,
        component: lazy(() => import("@/pages/Protected/SocialSupportBudget")),
        showSidebar: true,
        requiredAccessLevels: ["republic", "region", "city"],
      },
    ],
  },
  // {
  // path: "/events",
  // label: t("sidebar.events"),
  // icon: EventIcon,
  // iconActive: EventBlueIcon,
  // component: lazyWithReload(() => import("@/pages/Protected/Events")),
  // showSidebar: true,
  // },
  {
    path: "/opportunities",
    label: t("sidebar.events"),
    icon: EventIcon,
    iconActive: EventBlueIcon,
    showSidebar: true,
    children: [
      {
        path: "",
        label: t("sidebar.events"),
        icon: EventIcon,
        component: lazyWithReload(
          () => import("@/pages/Protected/Opportunities.temp"),
        ),
        showSidebar: false,
        index: true,
      },
      {
        path: ":id",
        label: t("sidebar.opportunityDetail"),
        icon: EventIcon,
        component: lazyWithReload(
          () => import("@/pages/Protected/OpportunityDetail.temp"),
        ),
        showSidebar: false,
      },
    ],
  },
  {
    path: "/youth-index",
    label: t("sidebar.youthIndex"),
    icon: IndexIcon,
    iconActive: IndexBlueIcon,
    showSidebar: true,
    children: [
      {
        path: "index",
        label: t("sidebar.youthIndexOverview"),
        icon: IndexIcon,
        component: lazyWithReload(() => import("@/pages/Protected/YouthIndex")),
        showSidebar: true,
      },
      {
        path: "indicator",
        label: t("sidebar.subIndicators"),
        icon: IndexIcon,
        component: lazyWithReload(
          () => import("@/pages/Protected/SubIndicators"),
        ),
        showSidebar: true,
      },
      {
        path: "survey-questions",
        label: t("sidebar.surveyQuestions"),
        icon: IndexIcon,
        component: lazyWithReload(
          () => import("@/pages/Protected/SurveyQuestions"),
        ),
        showSidebar: true,
      },
    ],
  },
  {
    path: "/youth-balance",
    label: t("sidebar.youthBalance"),
    icon: YouthIcon,
    iconActive: YouthBlueIcon,
    component: lazyWithReload(() => import("@/pages/Protected/YouthBalance")),
    showSidebar: true,
  },
  {
    path: "/career-edit",
    label: t("sidebar.careerEdit"),
    icon: EditIcon,
    showSidebar: true,
    children: [
      {
        path: "",
        label: t("sidebar.careerEdit"),
        icon: EditIcon,
        component: lazyWithReload(() => import("@/pages/Protected/CareerEdit")),
        showSidebar: false,
        index: true,
      },
      {
        path: "republic",
        label: t("sidebar.careerEdit"),
        icon: EditIcon,
        component: lazyWithReload(
          () => import("@/pages/Protected/CareerEditRepublic"),
        ),
        showSidebar: false,
      },
    ],
  },
  {
    path: "/yetakchi",
    label: "Yetakchi",
    icon: YouthIcon,
    iconActive: YouthBlueIcon,
    showSidebar: true,
    children: [
      // Admin nav items (republic, region, city access levels)
      {
        path: "admin",
        label: "Asosiy sahifa",
        icon: YouthIcon,
        showSidebar: false,
        requiredAccessLevels: ["republic", "region", "city"],
      },
      {
        path: "admin/mega-projects",
        label: "Mega loyihalar",
        icon: YouthIcon,
        showSidebar: false,
        requiredAccessLevels: ["republic", "region", "city"],
      },
      {
        path: "admin/tasks",
        label: "Topshiriqlar",
        icon: YouthIcon,
        showSidebar: true,
        requiredAccessLevels: ["republic", "region", "city"],
      },
      {
        path: "admin/reports",
        label: "Murojaatlar",
        icon: YouthIcon,
        showSidebar: true,
        requiredAccessLevels: ["republic", "region"],
      },
      {
        path: "admin/attendance",
        label: "Davomat",
        icon: YouthIcon,
        showSidebar: true,
        requiredAccessLevels: ["republic", "region", "city"],
      },
      {
        path: "admin/leaders",
        label: "Yetakchilar",
        icon: YouthIcon,
        showSidebar: false,
        requiredAccessLevels: ["republic", "region", "city"],
      },
      {
        path: "admin/ideas",
        label: "Tashabbuslar",
        icon: YouthIcon,
        showSidebar: true,
        requiredAccessLevels: ["republic", "region", "city"],
      },
      // {
      //   path: "admin/chat",
      //   label: "Chat",
      //   icon: YouthIcon,
      //   showSidebar: true,
      //   requiredAccessLevels: ["republic", "region", "city"],
      // },
      // {
      //   path: "admin/monitoring",
      //   label: "Monitoring",
      //   icon: YouthIcon,
      //   showSidebar: true,
      //   requiredAccessLevels: ["republic", "region", "city"],
      // },
      // Dashboard nav items (makhalla access level)
      {
        path: "dashboard/attendance",
        label: "Davomat",
        icon: YouthIcon,
        showSidebar: true,
        requiredAccessLevels: ["makhalla"],
      },
      {
        path: "dashboard/tasks",
        label: "Topshiriqlar",
        icon: YouthIcon,
        showSidebar: true,
        requiredAccessLevels: ["makhalla"],
      },
      // --- Institution-only: commented out ---
      // {
      //   path: "dashboard/mega-projects",
      //   label: "Mega loyihalar",
      //   icon: YouthIcon,
      //   showSidebar: true,
      //   requiredAccessLevels: ["makhalla"],
      // },
      {
        path: "dashboard/requests",
        label: "Murojaatlar",
        icon: YouthIcon,
        showSidebar: true,
        requiredAccessLevels: ["makhalla"],
      },
      // {
      //   path: "dashboard/balance",
      //   label: "Yoshlar balansi",
      //   icon: YouthIcon,
      //   showSidebar: true,
      //   requiredAccessLevels: ["makhalla"],
      // },
      {
        path: "dashboard/projects",
        label: "Tashabbuslar",
        icon: YouthIcon,
        showSidebar: true,
        requiredAccessLevels: ["makhalla"],
      },
      {
        path: "dashboard/profile",
        label: "Profil",
        icon: YouthIcon,
        showSidebar: false,
        requiredAccessLevels: ["makhalla"],
      },
      // {
      //   path: "dashboard/my-neighborhood",
      //   label: "Mening mahallam",
      //   icon: YouthIcon,
      //   showSidebar: true,
      //   requiredAccessLevels: ["makhalla"],
      // },
      // {
      //   path: "dashboard/youth-register",
      //   label: "Yoshlar ro'yxati",
      //   icon: YouthIcon,
      //   showSidebar: true,
      //   requiredAccessLevels: ["makhalla"],
      // },
      // {
      //   path: "dashboard/notification-news",
      //   label: "E'lonlar va yangiliklar",
      //   icon: YouthIcon,
      //   showSidebar: true,
      //   requiredAccessLevels: ["makhalla"],
      // },
    ],
  },
  {
    path: "/settings",
    label: t("sidebar.settings.label"),
    icon: SettingsIcon,
    iconActive: SettingsBlueIcon,
    // component is empty - MainLayout's Outlet will handle children
    showSidebar: true,
    children: [
      {
        path: "roles",
        label: t("sidebar.settings.roles"),
        icon: SettingsIcon,
        // No component property - defaults to OutletComponent via getRouteObjects
        showSidebar: true,
        children: [
          {
            path: "", // Index route for /settings/roles
            label: t("sidebar.settings.roles"),
            icon: SettingsIcon,
            component: lazyWithReload(
              () => import("@/pages/Protected/RolesManagement"),
            ),
            showSidebar: false,
            index: true,
          },
          {
            path: "create",
            label: t("sidebar.settings.createRole"),
            icon: SettingsIcon,
            component: lazyWithReload(
              () => import("@/pages/Protected/CreateRole"),
            ),
            showSidebar: false,
          },
          {
            path: ":roleId/edit",
            label: t("sidebar.settings.editRole"),
            icon: SettingsIcon,
            component: lazyWithReload(
              () => import("@/pages/Protected/EditRole"),
            ),
            showSidebar: false,
          },
        ],
      },
      {
        path: "activity-logs",
        label: t("sidebar.settings.activityLogs"),
        icon: SettingsIcon,
        component: lazyWithReload(
          () => import("@/pages/Protected/ActivityLogs"),
        ),
        showSidebar: true,
      },
    ],
  },
  {
    path: "/profile",
    label: "Profil",
    icon: SettingsIcon,
    component: lazyWithReload(() => import("@/pages/Protected/Profile")),
    showSidebar: false,
  },
  {
    path: "/yetakchi-ai",
    label: t("sidebar.leaderAI"),
    description: t("sidebar.leaderAIDescription"),
    icon: StarsIcon,
    component: lazyWithReload(() => import("@/pages/Protected/LeaderAI")),
    showSidebar: true,
  },
  {
    path: "/test-youth-list",
    label: "Test Youth List",
    icon: YouthListIcon,
    component: lazyWithReload(() => import("@/pages/Protected/TestYouthList")),
    showSidebar: false,
  },
];

// Keep mainRoutes for backward compatibility (will use default translations)
export const mainRoutes: RouteConfig[] = getMainRoutes((key: string) => key);

// Helper function to check if a pathname matches a route (including nested routes)
export const isRouteActive = (pathname: string, routePath: string): boolean => {
  // Exact match
  if (pathname === routePath) {
    return true;
  }
  // Check if pathname starts with route path followed by / (nested route)
  return pathname.startsWith(`${routePath}/`);
};

// Filter route children by access level
const filterRoutesByAccessLevel = (
  routes: RouteConfig[],
  accessLevel?: AccessLevel | null,
): RouteConfig[] =>
  routes
    .filter(
      (route) =>
        !route.requiredAccessLevels ||
        (accessLevel &&
          route.requiredAccessLevels.includes(accessLevel as AccessLevel)),
    )
    .map((route) =>
      route.children
        ? {
            ...route,
            children: filterRoutesByAccessLevel(route.children, accessLevel),
          }
        : route,
    );

// Filter routes by user ID
const filterRoutesByUserId = (
  routes: RouteConfig[],
  userId?: number | null,
): RouteConfig[] =>
  routes
    .filter(
      (route) =>
        !route.requiredUserIds ||
        (userId != null && route.requiredUserIds.includes(userId)),
    )
    .map((route) =>
      route.children
        ? {
            ...route,
            children: filterRoutesByUserId(route.children, userId),
          }
        : route,
    );

// Helper function to get sidebar items (only routes with showSidebar: true)
// Optionally filters by user permissions, account type, and access level
export const getSidebarItems = (
  t?: (key: string) => string,
  userPermissions?: string[],
  accountType?: string | null,
  accessLevel?: AccessLevel | null,
  userId?: number | null,
) => {
  const routes = t ? getMainRoutes(t) : mainRoutes;
  const hiddenSidebarPaths = new Set(["/opportunities", "/yetakchi-ai"]);
  let filteredRoutes = routes.filter(
    (route) =>
      route.showSidebar &&
      (!hiddenSidebarPaths.has(route.path) ||
        (route.requiredUserIds &&
          userId != null &&
          route.requiredUserIds.includes(userId))),
  );

  // Filter routes based on permissions and account type
  // Routes with requiredUserIds that match the current user bypass permission checks
  filteredRoutes = filteredRoutes.filter(
    (route) =>
      (route.requiredUserIds &&
        userId != null &&
        route.requiredUserIds.includes(userId)) ||
      shouldShowRoute(
        route.path,
        userPermissions || [],
        accountType as AccountType | null,
        accessLevel,
      ),
  );

  // Keep Settings visible for all users, but expose role management only to SUPER_ADMIN.
  filteredRoutes = filteredRoutes.map((route) => {
    if (route.path !== "/settings" || !route.children) {
      return route;
    }

    return {
      ...route,
      children: route.children.filter(
        (child) => child.path !== "roles" || accountType === "SUPER_ADMIN",
      ),
    };
  });

  // Filter routes and children based on access level
  filteredRoutes = filterRoutesByAccessLevel(filteredRoutes, accessLevel);

  // Filter routes based on user ID
  filteredRoutes = filterRoutesByUserId(filteredRoutes, userId);

  return filteredRoutes;
};

// Default Outlet component for routes without a component
const OutletComponent = () => {
  return <Outlet />;
};

// Simple loading fallback for route components
const RouteLoadingFallback = () => <div />;

// Helper function to convert routes to RouteObject format
export const getRouteObjects = (
  routes: RouteConfig[] = mainRoutes,
  t?: (key: string) => string,
): RouteObject[] => {
  const routesToUse = t ? getMainRoutes(t) : routes;
  return routesToUse.map((route) => {
    // Handle index route - if path is empty string, it's an index route
    if (route.path === "" || route.index) {
      if (!route.component) {
        throw new Error("Index route must have a component");
      }
      return {
        index: true,
        element: (
          <Suspense fallback={<RouteLoadingFallback />}>
            <route.component />
          </Suspense>
        ),
      };
    }

    // Regular route with path
    const routeObject: RouteObject = {
      path: route.path,
      // If no component, use Outlet (let MainLayout handle it)
      element: route.component ? (
        <Suspense fallback={<RouteLoadingFallback />}>
          <route.component />
        </Suspense>
      ) : (
        <OutletComponent />
      ),
    };

    // Recursively handle children routes
    if (route.children && route.children.length > 0) {
      let routeChildren = getRouteObjects(route.children, t);
      // Redirect /youth-index to /youth-index/index
      if (route.path === "/youth-index") {
        routeChildren = [
          { index: true, element: <Navigate to="index" replace /> },
          ...routeChildren,
        ];
      }
      // Redirect /settings to /settings/activity-logs
      if (route.path === "/settings") {
        routeChildren = [
          { index: true, element: <Navigate to="activity-logs" replace /> },
          ...routeChildren,
        ];
      }
      // Inject yetakchi admin and dashboard routes
      if (route.path === "/yetakchi") {
        routeObject.children = [...yetakchiAdmin, ...yetakchiDashboard];
      } else {
        routeObject.children = routeChildren;
      }
    }

    return routeObject;
  });
};
